import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import {
  type FooterConfig,
  footerConfigs,
  SUPPORT_CHAT_LINK,
} from '~/company/configs/footerConfig';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';
import { useSupportChat } from '~/hooks/useSupportChat';
import { CLUB_AUTH_NEXT_PARAM } from '~/modules/membership/domain/clubEntry';
import { useIsClubDisabled } from '~/modules/membership/ui/club/useClubEntry';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { userStore } from '~/stores';

const colClassName = company.match({
  mcosmetic: 'md:w-auto',
  default: 'md:w-[235.67px]',
});

interface FooterCDNavLinksProps
  extends Omit<React.ComponentPropsWithoutRef<'section'>, 'children'> {
  variant?: 'default' | 'light';
}

/**
 * Footer navigation columns. The club link is hidden when the company has no
 * club or, after mount, once the backend says the club is switched off.
 */
export const FooterCDNavLinks: React.FC<FooterCDNavLinksProps> = observer(
  ({ className, ...props }: FooterCDNavLinksProps) => {
    const { t } = useTranslation();
    const isClubDisabled = useIsClubDisabled();
    const { openModal } = useModal();
    const { openSupportChat } = useSupportChat('footer');

    const config: FooterConfig =
      footerConfigs[company.name] || footerConfigs.default;

    const totalColumns =
      config.sections.length + (config.contactColumn ? 1 : 0);

    const titleCn = cn(
      'mb-4 [font:var(--text-400-semibold)]',
      'text-navigation-text-primary',
    );

    const linkCn = cn(
      'block w-max no-underline [font:var(--text-200-regular-high)] transition-colors duration-200',
      'text-navigation-text-secondary! hover:text-navigation-text-primary!',
      company.match({ mcosmetic: '[&+&]:mt-2', default: '[&+&]:mt-3' }),
    );

    const handleAuthClick = (
      event: MouseEvent<HTMLAnchorElement>,
      redirect: string,
      isClub: boolean,
    ) => {
      if (isClub) {
        analyticsService.track(AnalyticsEvent.MEMBERSHIP_ENTRY_CLICKED, {
          source: 'footer',
          planCode: null,
        });
      }
      if (!userStore.isAuthorized) {
        openModal(
          ModalType.Auth,
          'footer',
          isClub ? { [CLUB_AUTH_NEXT_PARAM]: 'page' } : { redirect },
        );
        event.preventDefault();
      }
    };

    const isShown = (item: { club?: true }) =>
      !item.club || (company.isMembershipsEnabled && !isClubDisabled);

    const resolveLink = (link: string) => {
      if (link === '__blog__') return company.config.links.blog ?? '';
      return link;
    };

    return (
      <section {...props} className={cn('mt-8 md:mt-0', className)}>
        <div
          className={cn(
            'grid w-full grid-cols-2 gap-x-4 gap-y-8 md:gap-0',
            `md:grid-cols-${totalColumns}`,
          )}
        >
          {config.sections.map((section) => (
            <div key={section.title} className={colClassName}>
              <div className={titleCn}>{t(section.title)}</div>
              {section.items.filter(isShown).map((item) => {
                if (item.link === SUPPORT_CHAT_LINK) {
                  return (
                    <button
                      key={item.title}
                      type="button"
                      data-link={item.dataLink}
                      onClick={openSupportChat}
                      className={cn(
                        linkCn,
                        'cursor-pointer appearance-none border-none bg-transparent p-0 text-start',
                      )}
                    >
                      {t(item.title)}
                    </button>
                  );
                }

                const href = resolveLink(item.link);

                if (item.external) {
                  return (
                    <a
                      key={item.title}
                      data-link={item.dataLink}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkCn}
                    >
                      {t(item.title)}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.title}
                    data-link={item.dataLink}
                    to={href}
                    className={linkCn}
                    onClick={
                      item.requireAuth
                        ? (e) => handleAuthClick(e, href, item.club === true)
                        : undefined
                    }
                  >
                    {t(item.title)}
                  </Link>
                );
              })}
            </div>
          ))}

          {config.contactColumn && (
            <div className={colClassName}>
              <div className={titleCn}>{t('contactUs')}</div>
              {company.config.phoneNumber && (
                <a
                  href={`tel:${company.config.phoneNumber}`}
                  className={linkCn}
                >
                  {company.config.phoneNumber}
                </a>
              )}
              {company.config.contactEmail && (
                <a
                  href={`mailto:${company.config.contactEmail}`}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(linkCn, 'underline')}
                >
                  {company.config.contactEmail}
                </a>
              )}
              {company.hasAddressInFooter && (
                <span className={linkCn}>{company.config.address}</span>
              )}
              {company.showWorkingHoursInFooter && (
                <span className={cn(linkCn, 'text-navigation-text-tertiary!')}>
                  {t('workingHours', {
                    start: company.config.workingHours?.start,
                    end: company.config.workingHours?.end,
                  })}{' '}
                  {t('allDays')}
                </span>
              )}
            </div>
          )}
        </div>
      </section>
    );
  },
);
